import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mail, ArrowLeft } from 'lucide-react';
import { config } from '@/config';
import AnimatedBackground from '@/components/AnimatedBackground';

const ForgotPasswordPage = () => {
    const [email, setEmail] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [success, setSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setErrorMessage('');

        try {
            setIsLoading(true);
            await axios.post(`${config.backendUrl}/reset-password-request`, 
                { 
                    email: email.toLowerCase() 
                });
            setSuccess(true);
        } catch (error: any) {
            console.error('Password reset request failed:', error);
            setErrorMessage(error.response?.data?.message || 'Failed to send reset email');
        } finally {
            setIsLoading(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
                <AnimatedBackground />
                <div className="max-w-md w-full space-y-8 p-10 rounded-xl bg-white/90 backdrop-blur-sm shadow-xl">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-emerald-600 to-amber-500">
                            Foundation Elite Access
                        </h2>
                        <p className="mt-2 text-sm text-gray-600">
                            A password reset link has been sent to {email}.
                        </p>
                        <button
                            onClick={() => navigate('/login')}
                            className="mt-4 text-sm text-slate-800 hover:text-amber-500 font-medium flex items-center justify-center mx-auto transition-colors"
                        >
                            <ArrowLeft className="h-4 w-4 mr-1" />
                            Return to Foundation Elite
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
            <AnimatedBackground />
            <div className="max-w-md w-full space-y-8 p-10 rounded-xl bg-white/90 backdrop-blur-sm shadow-xl transform transition-all duration-300 hover:shadow-2xl">
                <div className="text-center">
                    <h2 className="mt-6 text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-slate-800 to-amber-500">
                        Restore Foundation Access
                    </h2>
                    <p className="mt-2 text-sm text-gray-600">
                        Enter your email address and we'll send you a secure password reset link
                    </p>
                </div>

                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    <div className="rounded-md">
                        <div className="group">
                            <div className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 group-focus-within:border-slate-800 group-focus-within:ring-1 group-focus-within:ring-amber-400 transition-all duration-200">
                                <Mail className="h-5 w-5 text-gray-400 group-focus-within:text-slate-800 transition-colors" />
                                <input
                                    id="email-address"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    className="flex-1 outline-none text-gray-900 placeholder-gray-500"
                                    placeholder="Email address"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>
                        </div>
                    </div>

                    {errorMessage && (
                        <div className="text-center text-red-500 text-sm animate-shake">
                            {errorMessage}
                        </div>
                    )}

                    <div className="flex flex-col space-y-4">
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-slate-800 to-slate-700 hover:from-slate-900 hover:to-amber-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-400 transition-all duration-200 transform hover:scale-[1.02] shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading ? 'Sending Foundation Access...' : 'Send Foundation Access'}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate('/login')}
                            className="text-sm text-slate-800 hover:text-amber-500 font-medium flex items-center justify-center transition-colors"
                        >
                            <ArrowLeft className="h-4 w-4 mr-1" />
                            Return to Foundation Elite
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ForgotPasswordPage; 
